import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import FormInput from "../../components/CustomComponents/Input/formInput";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/CustomComponents/Switchery/ToggleSwitch.css";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import DojoWebhookSecretHelpModal from "./DojoWebhookSecretHelpModal";

function AddPaymentGatewayModal({
  isModalOpen,
  setIsModalOpen,
  onPaymentGatewaySubmit,
  compID,
  paymentGatewayToEdit = null,
  setPaymentGateways,
}) {
  const [loading, setLoading] = useState(false);
  const [webhookHelpOpen, setWebhookHelpOpen] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [formData, setFormData] = useState({
    ID: 0,
    PGName: "",
    PGHostURL: "",
    PGAPIPriKey: "",
    PGAPISecKey: "",
    Status: true,
    CompID: compID,
    PlatformID: null,
    AllowGatewayRefunds: true,
  });

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const gatewayNameOptions = useMemo(() => {
    const options = platforms.map((p) => ({
      value: p.id,
      label: p.name || p.code,
      code: p.code,
      name: p.name,
      defaultHostURL: p.defaultHostURL,
    }));
    const existingId = paymentGatewayToEdit?.PlatformID;
    const existingName = paymentGatewayToEdit?.PGName?.trim();
    if (
      existingId &&
      !options.some((o) => o.value === existingId) &&
      existingName
    ) {
      options.push({ value: existingId, label: existingName, name: existingName });
    }
    return options;
  }, [platforms, paymentGatewayToEdit]);

  const selectedGatewayName =
    gatewayNameOptions.find((o) => o.value === formData.PlatformID) ||
    gatewayNameOptions.find(
      (o) => o.name?.toLowerCase() === (formData.PGName || "").trim().toLowerCase()
    ) ||
    null;

  const isDojoPlatform =
    (selectedGatewayName?.code || formData.PGName || "").toUpperCase() === "DOJO" ||
    (formData.PGName || "").toLowerCase() === "dojo";

  useEffect(() => {
    if (!isModalOpen) return;
    let cancelled = false;
    const loadPlatforms = async () => {
      try {
        const response = await axiosInstance.get(APIs.GET_ACTIVE_PAYMENT_GATEWAY_PLATFORMS);
        if (!cancelled && response.data.status && Array.isArray(response.data.data)) {
          setPlatforms(response.data.data);
        }
      } catch (err) {
        if (!cancelled) {
          showErrorToast("Failed to load payment gateway platforms");
        }
      }
    };
    loadPlatforms();
    return () => {
      cancelled = true;
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;

    if (paymentGatewayToEdit) {
      setFormData({
        ID: paymentGatewayToEdit.ID || 0,
        PGName: paymentGatewayToEdit.PGName || "",
        PGHostURL: paymentGatewayToEdit.PGHostURL || "",
        PGAPIPriKey: paymentGatewayToEdit.PGAPIPriKey || "",
        PGAPISecKey: paymentGatewayToEdit.PGAPISecKey || "",
        Status: paymentGatewayToEdit.Status !== false,
        CompID: compID,
        PlatformID: paymentGatewayToEdit.PlatformID || null,
        AllowGatewayRefunds: paymentGatewayToEdit.AllowGatewayRefunds !== false,
      });
    } else {
      setFormData({
        ID: 0,
        PGName: "",
        PGHostURL: "",
        PGAPIPriKey: "",
        PGAPISecKey: "",
        Status: true,
        CompID: compID,
        PlatformID: null,
        AllowGatewayRefunds: true,
      });
    }
  }, [paymentGatewayToEdit, compID, isModalOpen]);

  useEffect(() => {
    if (!isModalOpen || paymentGatewayToEdit || formData.PlatformID || platforms.length === 0)
      return;
    const first = platforms[0];
    setFormData((prev) => ({
      ...prev,
      PlatformID: first.id,
      PGName: first.name || first.code || prev.PGName,
      PGHostURL: prev.PGHostURL || first.defaultHostURL || "",
    }));
  }, [isModalOpen, paymentGatewayToEdit, platforms, formData.PlatformID]);

  const handleCloseModal = () => {
    setWebhookHelpOpen(false);
    setIsModalOpen(false);
    setFormData({
      ID: 0,
      PGName: "",
      PGHostURL: "",
      PGAPIPriKey: "",
      PGAPISecKey: "",
      Status: true,
      CompID: compID,
      PlatformID: null,
      AllowGatewayRefunds: true,
    });
  };

  const handleToggleStatus = () => {
    setFormData((prev) => ({ ...prev, Status: !prev.Status }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.PlatformID && (!formData.PGName || formData.PGName.trim().length === 0)) {
        showErrorToast("Please select a payment gateway");
        return;
      }

      if (!formData.PGHostURL || formData.PGHostURL.trim().length === 0) {
        showErrorToast("Please enter a valid host URL");
        return;
      }

      if (!formData.PGAPIPriKey || formData.PGAPIPriKey.trim().length === 0) {
        showErrorToast("Please enter a valid primary key");
        return;
      }

      if (!formData.PGAPISecKey || formData.PGAPISecKey.trim().length === 0) {
        showErrorToast("Please enter a valid secondary key");
        return;
      }

      setLoading(true);
      const payload = {
        ...formData,
        CompID: compID,
        EnteryDateTime: paymentGatewayToEdit
          ? formData.EntreyDateTime || new Date().toISOString()
          : new Date().toISOString(),
      };

      let response;
      if (paymentGatewayToEdit) {
        response = await axiosInstance.put(
          `${APIs.UPDATE_PAYMENT_GATEWAY}`,
          payload
        );

  
        if (response.data.status) {
      
          setPaymentGateways((prev) =>
            prev.map((gateway) =>
              gateway.id === formData.ID ? response.data.data : gateway
            )
          );
         }
      } else {
        response = await axiosInstance.post(
          APIs.POST_ADD_PAYMENT_GATEWAY,
          payload
        );

        if (response.data.status) {
          setPaymentGateways((prev) => [...prev, response.data.data]);
        }
      }

      if (response.data.status) {
        showSuccessToast(
          `Payment gateway ${
            paymentGatewayToEdit ? "updated" : "added"
          } successfully`
        );
        onPaymentGatewaySubmit(payload, !!paymentGatewayToEdit);
        setIsModalOpen(false);
        setFormData({
          ID: 0,
          PGName: "",
          PGHostURL: "",
          PGAPIPriKey: "",
          PGAPISecKey: "",
          Status: true,
          CompID: compID,
          PlatformID: null,
          AllowGatewayRefunds: true,
        });
      }
    } catch (err) {
      showErrorToast(
        `Failed to ${paymentGatewayToEdit ? "update" : "add"} payment gateway`
      );

    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGatewayNameChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      PlatformID: selectedOption ? selectedOption.value : null,
      PGName: selectedOption ? selectedOption.name || selectedOption.label : "",
      PGHostURL:
        prev.PGHostURL && prev.PGHostURL.trim().length > 0
          ? prev.PGHostURL
          : selectedOption?.defaultHostURL || "",
    }));
  };

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      onSubmit={handleSubmit}
      title={
        paymentGatewayToEdit ? "Update Payment Gateway" : "Add Payment Gateway"
      }
      subtitle={"Enter details to configure a payment gateway."}
      showButton={true}
      isCentered={true}
      modalSize={"lg"}
      submitButtonText={paymentGatewayToEdit ? "Update" : "Add"}
      themeColor={themeColor}
    >
      <GridSectionLoader loading={loading} minHeight="200px">
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <label
            className="form-label"
            style={{ color: isDarkMode ? "#ffffff" : "#000000" }}
          >
            Name <span className="text-danger">*</span>
          </label>
          <Select
            options={gatewayNameOptions}
            value={selectedGatewayName}
            onChange={handleGatewayNameChange}
            placeholder="Select Gateway"
            styles={dropdownStyles}
            isSearchable={false}
            isClearable={false}
            {...defaultSelectPortalProps}
          />
        </div>
        <div className="col-md-6">
          <FormInput
            label="Host URL"
            name="PGHostURL"
            type="text"
            value={formData.PGHostURL}
            placeHolder="Enter Host URL"
            onchange={handleInputChange}
            required
          />
        </div>
        <div className="col-md-6">
          <FormInput
            label="Primary Key"
            name="PGAPIPriKey"
            type="text"
            value={formData.PGAPIPriKey}
            placeHolder="Enter Primary Key"
            onchange={handleInputChange}
            required
          />
        </div>
        <div className="col-md-6">
          <label
            className="form-label d-flex align-items-center gap-2 mb-0"
            style={{ color: isDarkMode ? "#ffffff" : "#000000" }}
          >
            <button
              type="button"
              className="btn btn-link p-0 border-0 d-inline-flex align-items-center"
              onClick={() => setWebhookHelpOpen(true)}
              aria-label="How to get the webhook signing secret"
              title="How to get the Secondary Key"
              style={{
                color: themeColor,
                lineHeight: 1,
                minWidth: "auto",
              }}
            >
              <i className="fas fa-info-circle" style={{ fontSize: "1.1rem" }} aria-hidden />
            </button>
            <span>
              Secondary Key <span className="text-danger">*</span>
            </span>
          </label>
          <p
            className="small mb-2 mt-1"
            style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}
          >
            {isDojoPlatform ? "Dojo webhook signing secret" : "Webhook signing secret"}
          </p>
          <FormInput
            name="PGAPISecKey"
            type="text"
            value={formData.PGAPISecKey}
            placeHolder="Paste webhook signing secret from Dojo"
            onchange={handleInputChange}
            required
          />
        </div>
        <div className="col-md-6">
          <label
            className="form-label"
            style={{ color: isDarkMode ? "#ffffff" : "#000000" }}
          >
            Process refunds through gateway
          </label>
          <div className="d-flex align-items-center">
            <ToggleSwitch
              isChecked={formData.AllowGatewayRefunds}
              onToggle={() =>
                setFormData((prev) => ({
                  ...prev,
                  AllowGatewayRefunds: !prev.AllowGatewayRefunds,
                }))
              }
              label={formData.AllowGatewayRefunds ? "On" : "Off"}
              disabled={false}
              className="me-2"
            />
          </div>
          <p
            className="small mb-0 mt-1"
            style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}
          >
            Uses this Primary Key. No separate refund secret.
          </p>
        </div>
        <div className="col-md-6">
          <label
            className="form-label"
            style={{ color: isDarkMode ? "#ffffff" : "#000000" }}
          >
            Status
          </label>
          <div className="d-flex align-items-center">
            <ToggleSwitch
              isChecked={formData.Status}
              onToggle={handleToggleStatus}
              label={formData.Status ? "Active" : "Inactive"}
              disabled={false}
              className="me-2"
            />
          </div>
        </div>
      </div>
      </GridSectionLoader>

      <DojoWebhookSecretHelpModal
        isOpen={webhookHelpOpen}
        onClose={() => setWebhookHelpOpen(false)}
      />
    </CustomModal>
  );
}

export default AddPaymentGatewayModal;
